import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { SortOption } from './CommonTypes';
import './SortBar.css';

interface SortBarProps {
  availableColumns: Array<{
    field: string;
    label: string;
  }>;
  currentSorts: SortOption[];
  onSortsChange: (sorts: SortOption[]) => void;
  defaultSort?: SortOption;
  className?: string;
}

export const SortBar: React.FC<SortBarProps> = ({
  availableColumns,
  currentSorts,
  onSortsChange,
  defaultSort,
  className = ''
}) => {
  const [showAddMenu, setShowAddMenu] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const updateMenuPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // Use CSS custom properties instead of inline styles
      document.documentElement.style.setProperty('--sort-menu-top', `${rect.bottom + window.scrollY + 4}px`);
      document.documentElement.style.setProperty('--sort-menu-left', `${rect.left + window.scrollX}px`);
    }
  };

  const handleToggleMenu = () => {
    if (!showAddMenu) {
      updateMenuPosition();
    }
    setShowAddMenu(!showAddMenu);
  };

  // Update menu position on scroll or resize
  useEffect(() => {
    if (showAddMenu) {
      const handleScrollOrResize = () => {
        updateMenuPosition();
      };
      
      window.addEventListener('scroll', handleScrollOrResize);
      window.addEventListener('resize', handleScrollOrResize);
      
      return () => {
        window.removeEventListener('scroll', handleScrollOrResize);
        window.removeEventListener('resize', handleScrollOrResize);
      };
    }
  }, [showAddMenu]);

  const handleAddSort = (field: string) => {
    const column = availableColumns.find(col => col.field === field);
    if (!column) return;

    // Check if this field is already being sorted
    if (currentSorts.some(sort => sort.field === field)) {
      setShowAddMenu(false);
      return;
    }

    const newSort: SortOption = {
      field,
      direction: 'asc',
      label: column.label
    };

    onSortsChange([...currentSorts, newSort]);
    setShowAddMenu(false);
  };

  const handleRemoveSort = (field: string) => {
    onSortsChange(currentSorts.filter(sort => sort.field !== field));
  };

  const handleToggleDirection = (field: string) => {
    onSortsChange(currentSorts.map(sort => 
      sort.field === field 
        ? { ...sort, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
        : sort
    ));
  };

  const handleClearAll = () => {
    onSortsChange([]);
  };

  const getAvailableColumns = () => {
    return availableColumns.filter(col => 
      !currentSorts.some(sort => sort.field === col.field)
    );
  };

  const getSortIcon = (direction: 'asc' | 'desc') => {
    return direction === 'asc' ? '↑' : '↓';
  };

  return (
    <div className={`sort-bar ${className}`}>
      <div className="sort-items">
        {currentSorts.map((sort, index) => (
          <div key={sort.field} className="sort-item">
            <span className="sort-label">{sort.label}</span>
            <button
              type="button"
              className="sort-direction-button"
              onClick={() => handleToggleDirection(sort.field)}
              title={`Sort ${sort.direction === 'asc' ? 'descending' : 'ascending'}`}
            >
              {getSortIcon(sort.direction)}
            </button>
            <button
              type="button"
              className="sort-remove-button"
              onClick={() => handleRemoveSort(sort.field)}
              title="Remove sort"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <div className="sort-controls">
        <div className="sort-add-container">
          <button
            ref={buttonRef}
            type="button"
            className="app-btn app-btn-primary app-btn-small"
            onClick={handleToggleMenu}
            disabled={getAvailableColumns().length === 0}
            title="Add sort column"
          >
            +
          </button>
          
          {showAddMenu && createPortal(
            <div className="sort-add-menu sort-add-menu-positioned">
              {getAvailableColumns().map(column => (
                <button
                  key={column.field}
                  type="button"
                  className="sort-add-option"
                  onClick={() => handleAddSort(column.field)}
                >
                  {column.label}
                </button>
              ))}
            </div>,
            document.body
          )}
        </div>

        {currentSorts.length > 0 && (
          <button
            type="button"
            className="sort-clear-button"
            onClick={handleClearAll}
            title="Clear all sorts"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
};
